import axios from '@/utils/axios';
import {
  InAppPopupActionUser,
  InAppPopupActionUsersParams,
  InAppPopupActionUsersResponse,
  InAppPopup,
  InAppPopupDto,
  InAppPopupListParams,
  InAppPopupStats,
  InAppPopupStatsParams,
  InAppPopupUpdateDto,
} from '../types';

const getResponseData = <T>(payload: unknown): T => {
  const responsePayload = payload as { data?: T };

  if (responsePayload?.data !== undefined) {
    return responsePayload.data;
  }

  return payload as T;
};

const toRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' ? (value as Record<string, unknown>) : {};

const firstDefined = <T>(...values: T[]) =>
  values.find((value) => value !== undefined && value !== null);

const toNumber = (value: unknown, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : fallback;
  }

  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  return fallback;
};

const toNullableNumber = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = toNumber(value, Number.NaN);
  return Number.isFinite(parsed) ? parsed : null;
};

const toStringValue = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() ? value.trim() : null;

const toCollection = (payload: unknown, keys: string[] = []) => {
  if (Array.isArray(payload)) return payload;

  const record = toRecord(payload);
  const nestedData = record.data;

  if (Array.isArray(nestedData)) return nestedData;

  if (nestedData && typeof nestedData === 'object') {
    const nestedRecord = nestedData as Record<string, unknown>;
    const nestedArray = keys
      .map((key) => nestedRecord[key])
      .find((value) => Array.isArray(value));

    if (Array.isArray(nestedArray)) return nestedArray;
  }

  const directArray = keys
    .map((key) => record[key])
    .find((value) => Array.isArray(value));

  return Array.isArray(directArray) ? directArray : [];
};

const normalizeBreakdown = (value: unknown) =>
  (Array.isArray(value) ? value : []).map((item, index) => {
    const record = toRecord(item);

    return {
      key:
        toStringValue(
          firstDefined(record.key, record.id, record.action_type)
        ) ?? `breakdown-${index}`,
      label:
        toStringValue(firstDefined(record.label, record.button_label)) ??
        toStringValue(record.action_label),
      action_type: toStringValue(
        firstDefined(record.action_type, record.actionType)
      ),
      count: toNumber(firstDefined(record.count, record.total)),
    };
  });

const normalizeStatsItem = (item: unknown): InAppPopupStats | null => {
  const record = toRecord(item);
  const popupId =
    toStringValue(firstDefined(record.popup_id, record.popupId, record.id)) ??
    null;

  if (!popupId) return null;

  return {
    popup_id: popupId,
    impressions: toNumber(
      firstDefined(record.impressions, record.views, record.view_count)
    ),
    button_taps: toNumber(
      firstDefined(
        record.button_taps,
        record.buttonTaps,
        record.taps,
        record.tap_count
      )
    ),
    dismissals: toNumber(
      firstDefined(record.dismissals, record.dismisses, record.dismiss_count)
    ),
    action_users: toNumber(
      firstDefined(
        record.action_users,
        record.actionUsers,
        record.users,
        record.user_count
      )
    ),
    guest_action_users: toNullableNumber(
      firstDefined(record.guest_action_users, record.guestActionUsers)
    ),
    button_tap_breakdown: normalizeBreakdown(
      firstDefined(
        record.button_tap_breakdown,
        record.buttonTapBreakdown,
        record.button_breakdown,
        record.buttonBreakdown
      )
    ),
    dismiss_breakdown: normalizeBreakdown(
      firstDefined(record.dismiss_breakdown, record.dismissBreakdown)
    ),
  };
};

const normalizeStatsPayload = (payload: unknown) => {
  const responseData = getResponseData<unknown>(payload);

  if (
    responseData &&
    typeof responseData === 'object' &&
    !Array.isArray(responseData)
  ) {
    const record = responseData as Record<string, unknown>;

    if (record.popup_id || record.popupId) {
      const stats = normalizeStatsItem(record);
      return stats ? { [stats.popup_id]: stats } : {};
    }

    const collection = toCollection(record, ['stats', 'items', 'results']);

    if (collection.length) {
      return collection.reduce<Record<string, InAppPopupStats>>((acc, item) => {
        const stats = normalizeStatsItem(item);
        if (stats) acc[stats.popup_id] = stats;
        return acc;
      }, {});
    }

    return Object.entries(record).reduce<Record<string, InAppPopupStats>>(
      (acc, [popupId, value]) => {
        const stats = normalizeStatsItem({
          popup_id: popupId,
          ...(toRecord(value) as Record<string, unknown>),
        });

        if (stats) acc[stats.popup_id] = stats;
        return acc;
      },
      {}
    );
  }

  return toCollection(responseData, ['stats', 'items', 'results']).reduce<
    Record<string, InAppPopupStats>
  >((acc, item) => {
    const stats = normalizeStatsItem(item);
    if (stats) acc[stats.popup_id] = stats;
    return acc;
  }, {});
};

const normalizeMeta = (payload: unknown, fallbackLength: number) => {
  const record = toRecord(payload);
  const dataRecord = toRecord(record.data);
  const meta = toRecord(
    firstDefined(
      record.meta,
      record.pagination,
      dataRecord.meta,
      dataRecord.pagination
    )
  );
  const total = toNumber(
    firstDefined(meta.total, meta.total_count, meta.totalCount, meta.count),
    fallbackLength
  );
  const page = toNumber(firstDefined(meta.page, meta.current_page), 1);
  const limit = toNumber(
    firstDefined(meta.limit, meta.per_page),
    fallbackLength
  );
  const totalPages = toNullableNumber(
    firstDefined(meta.totalPages, meta.total_pages, meta.last_page)
  );

  return {
    total,
    page,
    limit,
    totalPages,
  };
};

const normalizeActionUser = (
  item: unknown,
  index: number
): InAppPopupActionUser => {
  const record = toRecord(item);
  const user = toRecord(firstDefined(record.user, record.users));
  const userId = toNullableNumber(
    firstDefined(record.user_id, record.userId, user.id)
  );
  const actionUserId = firstDefined(
    record.id,
    record.event_user_id,
    record.eventUserId
  );

  return {
    id:
      typeof actionUserId === 'string' || typeof actionUserId === 'number'
        ? actionUserId
        : userId ?? `popup-action-user-${index}`,
    user_id: userId,
    first_name: toStringValue(
      firstDefined(
        record.first_name,
        record.firstName,
        user.first_name,
        user.firstName
      )
    ),
    last_name: toStringValue(
      firstDefined(
        record.last_name,
        record.lastName,
        user.last_name,
        user.lastName
      )
    ),
    email: toStringValue(firstDefined(record.email, user.email)),
    user_type: firstDefined(
      record.user_type,
      record.userType,
      user.user_type,
      user.userType
    ) as number | string | null | undefined,
    event_type:
      toStringValue(firstDefined(record.event_type, record.eventType)) ??
      'button_tap',
    action_label: toStringValue(
      firstDefined(record.action_label, record.actionLabel, record.button_label)
    ),
    action_type: toStringValue(
      firstDefined(record.action_type, record.actionType)
    ),
    count: toNumber(firstDefined(record.count, record.total), 1),
    last_event_at: toStringValue(
      firstDefined(
        record.last_event_at,
        record.lastEventAt,
        record.last_seen_at,
        record.lastSeenAt,
        record.updated_at,
        record.created_at
      )
    ),
  };
};

export async function getInAppPopups(
  params?: InAppPopupListParams
): Promise<InAppPopup[]> {
  const response = await axios.get('/in-app-popups', { params });
  return getResponseData<InAppPopup[]>(response.data);
}

export async function getInAppPopup(id: string): Promise<InAppPopup> {
  const response = await axios.get(`/in-app-popups/${id}`);
  return getResponseData<InAppPopup>(response.data);
}

export async function createInAppPopup(
  popup: InAppPopupDto
): Promise<InAppPopup> {
  const response = await axios.post('/in-app-popups', popup);
  return getResponseData<InAppPopup>(response.data);
}

export async function updateInAppPopup(
  id: string,
  popup: InAppPopupUpdateDto
): Promise<InAppPopup> {
  const response = await axios.put(`/in-app-popups/${id}`, popup);
  return getResponseData<InAppPopup>(response.data);
}

export async function deleteInAppPopup(id: string) {
  const response = await axios.delete(`/in-app-popups/${id}`);
  return response.data;
}

export async function getInAppPopupStats({
  popupIds = [],
  platform,
}: InAppPopupStatsParams): Promise<Record<string, InAppPopupStats>> {
  if (!popupIds.length) return {};

  const response = await axios.get('/in-app-popups/stats', {
    params: {
      popup_ids: popupIds.join(','),
      platform,
    },
  });

  return normalizeStatsPayload(response.data);
}

export async function getInAppPopupActionUsers(
  popupId: string,
  params?: InAppPopupActionUsersParams
): Promise<InAppPopupActionUsersResponse> {
  const response = await axios.get(
    `/in-app-popups/${popupId}/stats/action-users`,
    {
      params: {
        page: params?.page,
        limit: params?.limit,
        event_type: params?.event_type || undefined,
      },
    }
  );
  const payload = response.data;
  const collection = toCollection(payload, ['users', 'items', 'results']);

  return {
    data: collection.map(normalizeActionUser),
    meta: normalizeMeta(payload, collection.length),
  };
}
